import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useAuth } from '../contexts/auth-context';
import DashboardLayout from '../layouts/dashboard-layout';
import Dashboard from '../pages/dashboard';
import NotFound from '../pages/not-found';
import Login from '../pages/login';
import LoadingSpinner from '../components/loading-spinner';

// Lazy-loaded routes for better performance
const Users = React.lazy(() => import('../pages/users'));
const CreateUser = React.lazy(() => import('../pages/users/create'));
const UserDetail = React.lazy(() => import('../pages/users/detail'));
const EditUser = React.lazy(() => import('../pages/users/edit'));
const UserPermissions = React.lazy(() => import('../pages/users/user-permissions'));
const Permissions = React.lazy(() => import('../pages/users/permissions'));
const CreatePermission = React.lazy(() => import('../pages/users/permissions/create'));
const EditPermission = React.lazy(() => import('../pages/users/permissions/edit'));
const CreatePermissionGroup = React.lazy(() => import('../pages/users/permission-groups/create'));
const EditPermissionGroup = React.lazy(() => import('../pages/users/permission-groups/edit'));
const TechnicalSupport = React.lazy(() => import('../pages/technical-support'));
const Languages = React.lazy(() => import('../pages/languages'));
const Settings = React.lazy(() => import('../pages/settings'));
const Statistics = React.lazy(() => import('../pages/statistics'));
// Chat pages
const ChatConversations = React.lazy(() => import('../pages/chat/conversations'));
const ChatOrganizations = React.lazy(() => import('../pages/chat/organizations'));


// Portal section pages
const ImamIntro = React.lazy(() => import('../pages/portal-sections/imam-intro'));
const Hadiths = React.lazy(() => import('../pages/portal-sections/hadiths'));
const Prayers = React.lazy(() => import('../pages/portal-sections/prayers'));
const Library = React.lazy(() => import('../pages/portal-sections/library'));
const Magazines = React.lazy(() => import('../pages/portal-sections/magazines'));
const Gallery = React.lazy(() => import('../pages/portal-sections/gallery'));
const News = React.lazy(() => import('../pages/portal-sections/news'));
const SpecialFiles = React.lazy(() => import('../pages/portal-sections/special-files'));
const Wiki = React.lazy(() => import('../pages/portal-sections/wiki'));
const HeartTalk = React.lazy(() => import('../pages/portal-sections/heart-talk'));
const ContactUs = React.lazy(() => import('../pages/portal-sections/contact-us'));

// Library sub-section pages
const Books = React.lazy(() => import('../pages/portal-sections/library/books'));
const BookDetail = React.lazy(() => import('../pages/portal-sections/library/books/detail'));
const EditBook = React.lazy(() => import('../pages/portal-sections/library/books/edit'));
const Authors = React.lazy(() => import('../pages/portal-sections/library/authors'));
const EditAuthor = React.lazy(() => import('../pages/portal-sections/library/authors/edit'));
const Publishers = React.lazy(() => import('../pages/portal-sections/library/publishers'));
const PublisherDetail = React.lazy(() => import('../pages/portal-sections/library/publishers/detail'));
const EditPublisher = React.lazy(() => import('../pages/portal-sections/library/publishers/edit'));
const Categories = React.lazy(() => import('../pages/portal-sections/library/categories'));
const CategoryDetail = React.lazy(() => import('../pages/portal-sections/library/categories/detail'));
const EditCategory = React.lazy(() => import('../pages/portal-sections/library/categories/edit'));
const BookFiles = React.lazy(() => import('../pages/portal-sections/library/book-files'));

// Hadiths sub-section pages
const HadithsList = React.lazy(() => import('../pages/portal-sections/hadiths/hadiths-list'));
const CreateHadith = React.lazy(() => import('../pages/portal-sections/hadiths/hadiths-list/create'));
const EditHadith = React.lazy(() => import('../pages/portal-sections/hadiths/hadiths-list/edit'));
const HadithDetail = React.lazy(() => import('../pages/portal-sections/hadiths/hadiths-list/detail'));
const HadisCategories = React.lazy(() => import('../pages/portal-sections/hadiths/categories'));
const Narrators = React.lazy(() => import('../pages/portal-sections/hadiths/narrators'));
const NarratorDetail = React.lazy(() => import('../pages/portal-sections/hadiths/narrators/detail'));
const EditNarrator = React.lazy(() => import('../pages/portal-sections/hadiths/narrators/edit'));
const References = React.lazy(() => import('../pages/portal-sections/hadiths/references'));
const HadithReferenceDetail = React.lazy(() => import('../pages/portal-sections/hadiths/references/detail'));
const EditHadithReference = React.lazy(() => import('../pages/portal-sections/hadiths/references/edit'));
const HadisParts = React.lazy(() => import('../pages/portal-sections/hadiths/hadis-parts'));
const HadisPartDetail = React.lazy(() => import('../pages/portal-sections/hadiths/hadis-parts/detail'));
const EditHadisPart = React.lazy(() => import('../pages/portal-sections/hadiths/hadis-parts/edit'));

// Special Files sub-section pages
const SpecialFilesList = React.lazy(() => import('../pages/portal-sections/special-files/special-files-list'));
const EditSpecialFile = React.lazy(() => import('../pages/portal-sections/special-files/special-files-list/edit'));

// Prayers sub-section pages
const PrayersList = React.lazy(() => import('../pages/portal-sections/prayers/prayers-list'));
const PrayerDetail = React.lazy(() => import('../pages/portal-sections/prayers/prayers-list/detail'));
const EditPrayer = React.lazy(() => import('../pages/portal-sections/prayers/prayers-list/edit'));
const PrayerCategories = React.lazy(() => import('../pages/portal-sections/prayers/categories'));
const EditPrayerCategory = React.lazy(() => import('../pages/portal-sections/prayers/categories/edit'));
const PrayerNarrators = React.lazy(() => import('../pages/portal-sections/prayers/narrators'));
const PrayerReciterDetail = React.lazy(() => import('../pages/portal-sections/prayers/narrators/detail'));
const EditPrayerReciter = React.lazy(() => import('../pages/portal-sections/prayers/narrators/edit'));
const PrayerReferences = React.lazy(() => import('../pages/portal-sections/prayers/references'));
const EditPrayerReference = React.lazy(() => import('../pages/portal-sections/prayers/references/edit'));
const PrayerParts = React.lazy(() => import('../pages/portal-sections/prayers/prayer-parts'));
const EditPrayerPart = React.lazy(() => import('../pages/portal-sections/prayers/prayer-parts/edit'));

// Magazines sub-section pages
const MagazinesList = React.lazy(() => import('../pages/portal-sections/magazines/magazines-list'));
const MagazineDetail = React.lazy(() => import('../pages/portal-sections/magazines/magazines-list/detail'));
const EditMagazine = React.lazy(() => import('../pages/portal-sections/magazines/magazines-list/edit'));

// Gallery sub-section pages
const MediaList = React.lazy(() => import('../pages/portal-sections/gallery/media'));
const MediaDetail = React.lazy(() => import('../pages/portal-sections/gallery/media/detail'));
const EditMedia = React.lazy(() => import('../pages/portal-sections/gallery/media/edit'));
const MediaCategories = React.lazy(() => import('../pages/portal-sections/gallery/categories'));
const MediaCategoryDetail = React.lazy(() => import('../pages/portal-sections/gallery/categories/detail'));
const EditMediaCategory = React.lazy(() => import('../pages/portal-sections/gallery/categories/edit'));

// News sub-section pages
const NewsList = React.lazy(() => import('../pages/portal-sections/news/news-list'));
const NewsDetail = React.lazy(() => import('../pages/portal-sections/news/news-list/detail'));
const EditNews = React.lazy(() => import('../pages/portal-sections/news/news-list/edit'));

// Wiki sub-section pages
const WikiList = React.lazy(() => import('../pages/portal-sections/wiki/wiki-list'));
const WikiDetail = React.lazy(() => import('../pages/portal-sections/wiki/wiki-list/detail'));
const EditWiki = React.lazy(() => import('../pages/portal-sections/wiki/wiki-list/edit'));
const WikiCategories = React.lazy(() => import('../pages/portal-sections/wiki/categories'));
const EditWikiCategory = React.lazy(() => import('../pages/portal-sections/wiki/categories/edit'));
const WikiAuthors = React.lazy(() => import('../pages/portal-sections/wiki/authors'));
const EditWikiAuthor = React.lazy(() => import('../pages/portal-sections/wiki/authors/edit'));
const WikiContents = React.lazy(() => import('../pages/portal-sections/wiki/wiki-contents'));
const WikiEditorTest = React.lazy(() => import('../pages/portal-sections/wiki/test'));

// Heart Talk sub-section pages
const HeartTalkList = React.lazy(() => import('../pages/portal-sections/heart-talk/heart-talk-list'));
const HeartTalkCategories = React.lazy(() => import('../pages/portal-sections/heart-talk/categories'));
const HeartTalkAuthors = React.lazy(() => import('../pages/portal-sections/heart-talk/authors'));
const EditHeartTalkAuthor = React.lazy(() => import('../pages/portal-sections/heart-talk/authors/edit'));

// Astan Structures pages
const AstanStructures = React.lazy(() => import('../pages/portal-sections/astan-structures'));
const EditAstanStructure = React.lazy(() => import('../pages/portal-sections/astan-structures/edit'));

// Protected route component
const ProtectedRoute: React.FC<{
  component: React.ComponentType<any>;
  path: string;
  exact?: boolean;
}> = ({ component: Component, ...rest }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <DashboardLayout>
            <React.Suspense fallback={<LoadingSpinner />}>
              <Component {...props} />
            </React.Suspense>
          </DashboardLayout>
        ) : (
          <Redirect to="/login" />
        )
      }
    />
  );
};

const Routes = () => {
  return (
    <Switch>
        {/* Dashboard */}
        <ProtectedRoute path="/dashboard" exact component={Dashboard} />
        
        {/* Main routes */}
        <ProtectedRoute path="/users" exact component={Users} />
        <ProtectedRoute path="/users/create" exact component={CreateUser} />
        <ProtectedRoute path="/users/permissions" exact component={Permissions} />
        <ProtectedRoute path="/users/permissions/create" exact component={CreatePermission} />
        <ProtectedRoute path="/users/permissions/:id/edit" exact component={EditPermission} />
        <ProtectedRoute path="/users/permission-groups/create" exact component={CreatePermissionGroup} />
        <ProtectedRoute path="/users/permission-groups/:id/edit" exact component={EditPermissionGroup} />
        <ProtectedRoute path="/users/:id" exact component={UserDetail} />
        <ProtectedRoute path="/users/:id/edit" exact component={EditUser} />
        <ProtectedRoute path="/users/:id/permissions" exact component={UserPermissions} />
        <ProtectedRoute path="/technical-support" exact component={TechnicalSupport} />
        <ProtectedRoute path="/languages" exact component={Languages} />
        <ProtectedRoute path="/settings" exact component={Settings} />
        <ProtectedRoute path="/statistics" exact component={Statistics} />
        
        {/* Chat */}
        <ProtectedRoute path="/chat/conversations" exact component={ChatConversations} />
        <ProtectedRoute path="/chat/organizations" exact component={ChatOrganizations} />
        
        {/* Portal sections */}
        <ProtectedRoute path="/portal-sections/imam-intro" exact component={ImamIntro} />
        <ProtectedRoute path="/portal-sections/imam-intro/create" exact component={ImamIntro} />
        <ProtectedRoute path="/portal-sections/imam-intro/:id/edit" exact component={ImamIntro} />
        <ProtectedRoute path="/portal-sections/imam-intro/:id" exact component={ImamIntro} />
        <ProtectedRoute path="/portal-sections/hadiths" exact component={Hadiths} />
        <ProtectedRoute path="/portal-sections/prayers" exact component={Prayers} />
        <ProtectedRoute path="/portal-sections/magazines" exact component={Magazines} />
        <ProtectedRoute path="/portal-sections/gallery" exact component={Gallery} />
        <ProtectedRoute path="/portal-sections/news" exact component={News} />
        <ProtectedRoute path="/portal-sections/special-files" exact component={SpecialFiles} />
        <ProtectedRoute path="/portal-sections/wiki" exact component={Wiki} />
        <ProtectedRoute path="/portal-sections/heart-talk" exact component={HeartTalk} />
        <ProtectedRoute path="/portal-sections/contact-us" exact component={ContactUs} />
        
        {/* Library routes */}
        <ProtectedRoute path="/library" exact component={Library} />
        <ProtectedRoute path="/library/books/:id/edit" exact component={EditBook} />
        <ProtectedRoute path="/library/books" exact component={Books} />
        <ProtectedRoute path="/library/authors/:id/edit" exact component={EditAuthor} />
        <ProtectedRoute path="/library/authors" exact component={Authors} />
        <ProtectedRoute path="/library/publishers" exact component={Publishers} />
        <ProtectedRoute path="/library/publishers/create" exact component={EditPublisher} />
        <ProtectedRoute path="/library/publishers/:id" exact component={PublisherDetail} />
        <ProtectedRoute path="/library/publishers/:id/edit" exact component={EditPublisher} />
        <ProtectedRoute path="/library/categories/:id/edit" exact component={EditCategory} />
        <ProtectedRoute path="/library/categories/create" exact component={EditCategory} />
        <ProtectedRoute path="/library/categories" exact component={Categories} />
        <ProtectedRoute path="/library/book-files" exact component={BookFiles} />
        
        {/* Legacy portal-sections routes (redirects) */}
        <ProtectedRoute path="/portal-sections/library" exact component={Library} />
        <ProtectedRoute path="/portal-sections/library/books/create" exact component={EditBook} />
        <ProtectedRoute path="/portal-sections/library/books/:id/edit" exact component={EditBook} />
        <ProtectedRoute path="/portal-sections/library/books/:id" exact component={BookDetail} />
        <ProtectedRoute path="/portal-sections/library/books" exact component={Books} />
        <ProtectedRoute path="/portal-sections/library/authors/:id/edit" exact component={EditAuthor} />
        <ProtectedRoute path="/portal-sections/library/authors" exact component={Authors} />
        <ProtectedRoute path="/portal-sections/library/publishers" exact component={Publishers} />
        <ProtectedRoute path="/portal-sections/library/publishers/create" exact component={EditPublisher} />
        <ProtectedRoute path="/portal-sections/library/publishers/:id" exact component={PublisherDetail} />
        <ProtectedRoute path="/portal-sections/library/publishers/:id/edit" exact component={EditPublisher} />
        <ProtectedRoute path="/portal-sections/library/categories/:id/edit" exact component={EditCategory} />
        <ProtectedRoute path="/portal-sections/library/categories/create" exact component={EditCategory} />
        <ProtectedRoute path="/portal-sections/library/categories/:id" exact component={CategoryDetail} />
        <ProtectedRoute path="/portal-sections/library/categories" exact component={Categories} />
        <ProtectedRoute path="/portal-sections/library/book-files" exact component={BookFiles} />

        {/* Hadiths routes */}
        <ProtectedRoute path="/portal-sections/hadiths/hadiths-list/create" exact component={CreateHadith} />
        <ProtectedRoute path="/portal-sections/hadiths/hadiths-list/:id/edit" exact component={EditHadith} />
        <ProtectedRoute path="/portal-sections/hadiths/hadiths-list/:id" exact component={HadithDetail} />
        <ProtectedRoute path="/portal-sections/hadiths/hadiths-list" exact component={HadithsList} />
        <ProtectedRoute path="/portal-sections/hadiths/hadiths" exact component={HadithsList} /> {/* Alias for hadiths-list */}
        <ProtectedRoute path="/portal-sections/hadiths/categories" exact component={HadisCategories} />
        <ProtectedRoute path="/portal-sections/hadiths/narrators/create" exact component={EditNarrator} />
        <ProtectedRoute path="/portal-sections/hadiths/narrators/:id/edit" exact component={EditNarrator} />
        <ProtectedRoute path="/portal-sections/hadiths/narrators/:id" exact component={NarratorDetail} />
        <ProtectedRoute path="/portal-sections/hadiths/narrators" exact component={Narrators} />
        <ProtectedRoute path="/portal-sections/hadiths/references/create" exact component={EditHadithReference} />
        <ProtectedRoute path="/portal-sections/hadiths/references/:id/edit" exact component={EditHadithReference} />
        <ProtectedRoute path="/portal-sections/hadiths/references/:id" exact component={HadithReferenceDetail} />
        <ProtectedRoute path="/portal-sections/hadiths/references" exact component={References} />
        <ProtectedRoute path="/portal-sections/hadiths/hadis-parts/create" exact component={EditHadisPart} />
        <ProtectedRoute path="/portal-sections/hadiths/hadis-parts/:id/edit" exact component={EditHadisPart} />
        <ProtectedRoute path="/portal-sections/hadiths/hadis-parts/:id" exact component={HadisPartDetail} />
        <ProtectedRoute path="/portal-sections/hadiths/hadis-parts" exact component={HadisParts} />

        {/* Special Files routes */}
        <ProtectedRoute path="/portal-sections/special-files/special-files-list/:id/edit" exact component={EditSpecialFile} />
        <ProtectedRoute path="/portal-sections/special-files/special-files-list/create" exact component={EditSpecialFile} />
        <ProtectedRoute path="/portal-sections/special-files/special-files-list" exact component={SpecialFilesList} />
        <ProtectedRoute path="/portal-sections/special-files/special-files" exact component={SpecialFilesList} /> {/* Alias for special-files-list */}

        {/* Prayers routes */}
        <ProtectedRoute path="/portal-sections/prayers/prayers-list/create" exact component={EditPrayer} />
        <ProtectedRoute path="/portal-sections/prayers/prayers-list/:id/edit" exact component={EditPrayer} />
        <ProtectedRoute path="/portal-sections/prayers/prayers-list/:id" exact component={PrayerDetail} />
        <ProtectedRoute path="/portal-sections/prayers/prayers-list" exact component={PrayersList} />
        <ProtectedRoute path="/portal-sections/prayers/prayers" exact component={PrayersList} /> {/* Alias for prayers-list */}
        <ProtectedRoute path="/portal-sections/prayers/categories/:id/edit" exact component={EditPrayerCategory} />
        <ProtectedRoute path="/portal-sections/prayers/categories/create" exact component={EditPrayerCategory} />
        <ProtectedRoute path="/portal-sections/prayers/categories" exact component={PrayerCategories} />
        <ProtectedRoute path="/portal-sections/prayers/narrators/:id/edit" exact component={EditPrayerReciter} />
        <ProtectedRoute path="/portal-sections/prayers/narrators/create" exact component={EditPrayerReciter} />
        <ProtectedRoute path="/portal-sections/prayers/narrators/:id" exact component={PrayerReciterDetail} />
        <ProtectedRoute path="/portal-sections/prayers/narrators" exact component={PrayerNarrators} />
        <ProtectedRoute path="/portal-sections/prayers/references/:id/edit" exact component={EditPrayerReference} />
        <ProtectedRoute path="/portal-sections/prayers/references/create" exact component={EditPrayerReference} />
        <ProtectedRoute path="/portal-sections/prayers/references" exact component={PrayerReferences} />
        <ProtectedRoute path="/portal-sections/prayers/prayer-parts/:id/edit" exact component={EditPrayerPart} />
        <ProtectedRoute path="/portal-sections/prayers/prayer-parts/create" exact component={EditPrayerPart} />
        <ProtectedRoute path="/portal-sections/prayers/prayer-parts" exact component={PrayerParts} />

        {/* Magazines routes */}
        <ProtectedRoute path="/portal-sections/magazines/magazines-list/:id/edit" exact component={EditMagazine} />
        <ProtectedRoute path="/portal-sections/magazines/magazines-list/create" exact component={EditMagazine} />
        <ProtectedRoute path="/portal-sections/magazines/magazines-list/:id" exact component={MagazineDetail} />
        <ProtectedRoute path="/portal-sections/magazines/magazines-list" exact component={MagazinesList} />
        <ProtectedRoute path="/portal-sections/magazines/magazines" exact component={MagazinesList} /> {/* Alias for magazines-list */}

        {/* Gallery routes */}
        <ProtectedRoute path="/portal-sections/gallery/media/create" exact component={EditMedia} />
        <ProtectedRoute path="/portal-sections/gallery/media/:id/edit" exact component={EditMedia} />
        <ProtectedRoute path="/portal-sections/gallery/media/:id" exact component={MediaDetail} />
        <ProtectedRoute path="/portal-sections/gallery/media" exact component={MediaList} />
        <ProtectedRoute path="/portal-sections/gallery/categories/create" exact component={EditMediaCategory} />
        <ProtectedRoute path="/portal-sections/gallery/categories/:id/edit" exact component={EditMediaCategory} />
        <ProtectedRoute path="/portal-sections/gallery/categories/:id" exact component={MediaCategoryDetail} />
        <ProtectedRoute path="/portal-sections/gallery/categories" exact component={MediaCategories} />

        {/* Astan Structures routes */}
        <ProtectedRoute path="/portal-sections/astan-structures/create" exact component={EditAstanStructure} />
        <ProtectedRoute path="/portal-sections/astan-structures/:id/edit" exact component={EditAstanStructure} />
        <ProtectedRoute path="/portal-sections/astan-structures" exact component={AstanStructures} />

        {/* News routes */}
        <ProtectedRoute path="/portal-sections/news/news-list/create" exact component={EditNews} />
        <ProtectedRoute path="/portal-sections/news/news-list/:id/edit" exact component={EditNews} />
        <ProtectedRoute path="/portal-sections/news/news-list/:id" exact component={NewsDetail} />
        <ProtectedRoute path="/portal-sections/news/news-list" exact component={NewsList} />
        <ProtectedRoute path="/portal-sections/news/news" exact component={NewsList} /> {/* Alias for news-list */}

        {/* Wiki routes - Order matters! More specific routes first */}
        <ProtectedRoute path="/portal-sections/wiki/test" exact component={WikiEditorTest} />
        <ProtectedRoute path="/portal-sections/wiki/wiki-list/create" exact component={EditWiki} />
        <ProtectedRoute path="/portal-sections/wiki/wiki-list/:id/edit" exact component={EditWiki} />
        <ProtectedRoute path="/portal-sections/wiki/wiki-list/:id" exact component={WikiDetail} />
        <ProtectedRoute path="/portal-sections/wiki/wiki-list" exact component={WikiList} />
        <ProtectedRoute path="/portal-sections/wiki/wiki" exact component={WikiList} /> {/* Alias for wiki-list */}
        <ProtectedRoute path="/portal-sections/wiki/wiki-contents" exact component={WikiContents} />
        <ProtectedRoute path="/portal-sections/wiki/categories/:id/edit" exact component={EditWikiCategory} />
        <ProtectedRoute path="/portal-sections/wiki/categories/create" exact component={EditWikiCategory} />
        <ProtectedRoute path="/portal-sections/wiki/categories" exact component={WikiCategories} />
        <ProtectedRoute path="/portal-sections/wiki/authors/:id/edit" exact component={EditWikiAuthor} />
        <ProtectedRoute path="/portal-sections/wiki/authors/create" exact component={EditWikiAuthor} />
        <ProtectedRoute path="/portal-sections/wiki/authors" exact component={WikiAuthors} />

        {/* Heart Talk routes */}
        <ProtectedRoute path="/portal-sections/heart-talk/heart-talk-list" exact component={HeartTalkList} />
        <ProtectedRoute path="/portal-sections/heart-talk/categories" exact component={HeartTalkCategories} />
        <ProtectedRoute path="/portal-sections/heart-talk/authors/:id/edit" exact component={EditHeartTalkAuthor} />
        <ProtectedRoute path="/portal-sections/heart-talk/authors/create" exact component={EditHeartTalkAuthor} />
        <ProtectedRoute path="/portal-sections/heart-talk/authors" exact component={HeartTalkAuthors} />

        {/* Login route */}
        <Route path="/login" exact component={Login} />

        {/* Default redirect */}
        <Route path="/" exact>
          <Redirect to="/dashboard" />
        </Route>
        
        {/* 404 */}
        <Route component={NotFound} />
      </Switch>
  );
};

export default Routes;
